import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { customerAuthAPI } from '../../services/api';
import { MailCheck, RefreshCw } from 'lucide-react';

export default function VerifyEmail() {
    const { customer, updateCustomer } = useCustomerAuth();
    const navigate = useNavigate();
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0);
    const inputRefs = useRef([]);

    useEffect(() => {
        if (!customer) {
            navigate('/giris');
        } else if (customer.emailVerified) {
            navigate('/');
        }
    }, [customer, navigate]);

    useEffect(() => {
        if (resendCountdown > 0) {
            const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCountdown]);

    const handleChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const newCode = [...code];
        newCode[index] = value.slice(-1);
        setCode(newCode);
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pasted.length === 6) {
            setCode(pasted.split(''));
            inputRefs.current[5]?.focus();
        }
    };

    const handleVerify = async () => {
        const fullCode = code.join('');
        if (fullCode.length !== 6) {
            return setError('Lütfen 6 haneli doğrulama kodunu girin');
        }
        setError('');
        setLoading(true);
        try {
            const res = await customerAuthAPI.verifyEmail({ code: fullCode });
            setSuccess('E-posta adresiniz doğrulandı!');
            updateCustomer(res.data.customer);
            setTimeout(() => navigate('/'), 1500);
        } catch (err) {
            setError(err.response?.data?.error || 'Doğrulama başarısız oldu');
        }
        setLoading(false);
    };

    const handleResend = async () => {
        setError('');
        setResendLoading(true);
        try {
            await customerAuthAPI.resendVerification();
            setSuccess('Yeni doğrulama kodu e-posta adresinize gönderildi');
            setResendCountdown(60);
            setCode(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } catch (err) {
            setError(err.response?.data?.error || 'Kod gönderilemedi');
        }
        setResendLoading(false);
    };

    if (!customer) return null;

    return (
        <div className="auth-page">
            <div className="auth-container">
                <div className="auth-header">
                    <MailCheck size={40} />
                    <h1>E-posta Doğrulama</h1>
                    <p><strong>{customer.email}</strong> adresine gönderilen 6 haneli kodu girin</p>
                </div>

                {error && <div className="auth-error">{error}</div>}
                {success && <div className="auth-success">{success}</div>}

                <div className="verify-code-inputs" onPaste={handlePaste}>
                    {code.map((digit, i) => (
                        <input
                            key={i}
                            ref={el => inputRefs.current[i] = el}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={e => handleChange(i, e.target.value)}
                            onKeyDown={e => handleKeyDown(i, e)}
                            className="verify-code-input"
                            autoFocus={i === 0}
                        />
                    ))}
                </div>

                <button
                    className="btn btn-primary auth-submit"
                    onClick={handleVerify}
                    disabled={loading || code.join('').length !== 6}
                >
                    {loading ? 'Doğrulanıyor...' : 'Doğrula'}
                </button>

                <div className="verify-resend">
                    <p>Kod gelmedi mi?</p>
                    <button
                        className="verify-resend-btn"
                        onClick={handleResend}
                        disabled={resendLoading || resendCountdown > 0}
                    >
                        <RefreshCw size={14} />
                        {resendCountdown > 0 ? `Tekrar gönder (${resendCountdown}s)` : 'Tekrar Gönder'}
                    </button>
                </div>
            </div>
        </div>
    );
}
